import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, Box, Typography, InputAdornment, Fade, Divider, CircularProgress,
  IconButton, Tooltip, Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Business, Link as LinkIcon, Person, Email, Lock, ContentCopy, CheckCircle,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { createTenant } from '../../services/tenants';

// Espelha TenantController::SLUG_REGEX (alodesk-chat-api) -- validação
// client-side só para feedback imediato; a validação que vale de verdade é
// sempre a do servidor.
const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// Espelha TenantController::RESERVED_SLUGS. Mantido em sincronia manualmente
// -- se a lista mudar no backend, o pior caso é o front deixar passar um
// slug que o servidor ainda vai recusar (o form mostra o erro 422 da API).
const RESERVED_SLUGS = [
  'api', 'auth', 'admin', 'super-admin', 'app', 'www', 'assets',
  'static', 'public', 'null', 'undefined', 'true', 'false',
  'tenants', 'tenant', 'users', 'user', 'login', 'logout',
  'register', 'password', 'test', 'staging',
];

const CHAT_DOMAIN = 'chat.alodesk.com.br';

// Remove acentos/diacríticos via NFD + faixa unicode de "combining marks"
// (̀-ͯ), escrita com escape de código em vez do caractere literal
// para não depender de encoding do editor/terminal.
const DIACRITICS_REGEX = new RegExp('[̀-ͯ]', 'g');

function slugify(value) {
  return (value || '')
    .normalize('NFD').replace(DIACRITICS_REGEX, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

function validateSlug(slug) {
  if (!slug) return 'O slug é obrigatório.';
  if (slug.length > 120) return 'O slug deve ter no máximo 120 caracteres.';
  if (!SLUG_REGEX.test(slug)) return 'Use apenas letras minúsculas, números e hífen simples (ex.: minha-empresa).';
  if (RESERVED_SLUGS.includes(slug)) return 'Este slug é reservado e não pode ser usado.';
  if (/^[0-9]+$/.test(slug)) return 'O slug não pode ser composto apenas por números.';
  return '';
}

const emptyForm = {
  name: '',
  slug: '',
  admin_name: '',
  admin_email: '',
  admin_password: '',
};

const CreateTenantDialog = ({ open, onClose, onCreated }) => {
  const theme = useTheme();
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Resultado da criação: mostra a senha gerada (se houver) UMA ÚNICA VEZ.
  // Nunca guardado em localStorage, nunca logado -- só vive neste state, que
  // é descartado ao fechar o diálogo.
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setForm(emptyForm);
    setSlugTouched(false);
    setErrors({});
    setResult(null);
    setCopied(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleNameChange = (value) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }));
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, slug: slugify(value) }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'O nome da empresa é obrigatório.';
    const slugError = validateSlug(form.slug);
    if (slugError) next.slug = slugError;
    if (!form.admin_name.trim()) next.admin_name = 'O nome do administrador é obrigatório.';
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email.trim());
    if (!emailOk) next.admin_email = 'Informe um e-mail válido.';
    if (form.admin_password && form.admin_password.length < 6) {
      next.admin_password = 'A senha deve ter ao menos 6 caracteres.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug,
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim(),
      };
      if (form.admin_password) {
        payload.admin_password = form.admin_password;
      }
      const data = await createTenant(payload);
      toast.success(`Empresa "${data.tenant?.name || form.name}" criada com sucesso.`, {
        position: toast.POSITION.BOTTOM_CENTER,
      });
      setResult(data);
      onCreated?.();
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors) {
        const firstMsgs = {};
        Object.entries(apiErrors).forEach(([field, msgs]) => {
          const key = field === 'slug' ? 'slug'
            : field === 'name' ? 'name'
              : field === 'admin_name' ? 'admin_name'
                : field === 'admin_email' ? 'admin_email'
                  : field === 'admin_password' ? 'admin_password'
                    : null;
          if (key) firstMsgs[key] = Array.isArray(msgs) ? msgs[0] : String(msgs);
        });
        setErrors((prev) => ({ ...prev, ...firstMsgs }));
      }
      const msg = error?.response?.data?.error || 'Não foi possível criar a empresa.';
      toast.error(msg, { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.generated_password) return;
    try {
      await navigator.clipboard.writeText(result.generated_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Não foi possível copiar automaticamente. Selecione o texto manualmente.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          margin: 3,
          maxHeight: 'calc(100vh - 48px)',
        },
      }}
    >
      <DialogTitle sx={{
        textAlign: 'center',
        pb: 2,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{
          display: 'inline-flex',
          p: 1.5,
          borderRadius: '50%',
          backgroundColor: result ? theme.palette.success.main : theme.palette.primary.main,
          mb: 2,
        }}>
          {result ? <CheckCircle sx={{ fontSize: 28, color: 'white' }} /> : <Business sx={{ fontSize: 28, color: 'white' }} />}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {result ? 'Empresa criada' : 'Nova empresa'}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {result
            ? 'Guarde os dados de acesso do primeiro administrador agora.'
            : 'Cria o tenant e o primeiro administrador que vai gerenciar essa empresa.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 4, pt: 4, bgcolor: theme.palette.background.default }}>
        {result ? (
          <Fade in timeout={300}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                URL pública do chat: <strong>{CHAT_DOMAIN}/{result.tenant?.slug}</strong>
              </Alert>
              <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Primeiro administrador
                </Typography>
                <Typography variant="body2"><strong>Nome:</strong> {result.admin?.name}</Typography>
                <Typography variant="body2"><strong>E-mail:</strong> {result.admin?.email}</Typography>
              </Box>

              {result.generated_password ? (
                <Alert
                  severity="warning"
                  sx={{ borderRadius: 2 }}
                  icon={<Lock fontSize="inherit" />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Senha temporária gerada — exibida apenas agora
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    Ela não fica salva em nenhum lugar (nem nesta tela depois de fechada). Copie e repasse ao administrador com segurança.
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                  }}>
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5, flex: 1, wordBreak: 'break-all' }}
                    >
                      {result.generated_password}
                    </Typography>
                    <Tooltip title={copied ? 'Copiado!' : 'Copiar senha'}>
                      <IconButton size="small" onClick={copyPassword}>
                        {copied ? <CheckCircle fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Senha definida manualmente na criação — nenhuma senha temporária foi gerada.
                </Alert>
              )}
            </Box>
          </Fade>
        ) : (
          <Fade in timeout={300}>
            <Box sx={{ px: 0.5 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Nome da empresa"
                    value={form.name}
                    onChange={({ target }) => handleNameChange(target.value)}
                    required
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name || ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Business color="action" /></InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Slug (identifica a empresa na URL pública do chat)"
                    value={form.slug}
                    onChange={({ target }) => handleSlugChange(target.value)}
                    required
                    fullWidth
                    error={!!errors.slug}
                    helperText={errors.slug || `URL do chat: ${CHAT_DOMAIN}/${form.slug || 'sua-empresa'}`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><LinkIcon color="action" /></InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
                  />
                </Grid>

                <Grid item xs={12}><Divider textAlign="left">Primeiro admin</Divider></Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Nome"
                    value={form.admin_name}
                    onChange={({ target }) => setForm((p) => ({ ...p, admin_name: target.value }))}
                    required
                    fullWidth
                    error={!!errors.admin_name}
                    helperText={errors.admin_name || ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Person color="action" /></InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="E-mail"
                    type="email"
                    value={form.admin_email}
                    onChange={({ target }) => setForm((p) => ({ ...p, admin_email: target.value }))}
                    required
                    fullWidth
                    error={!!errors.admin_email}
                    helperText={errors.admin_email || ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Email color="action" /></InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Senha temporária (opcional)"
                    type="password"
                    value={form.admin_password}
                    onChange={({ target }) => setForm((p) => ({ ...p, admin_password: target.value }))}
                    fullWidth
                    error={!!errors.admin_password}
                    helperText={errors.admin_password || 'Deixe em branco para gerar uma senha automaticamente — ela será exibida uma única vez após a criação.'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, px: 6, borderTop: '1px solid', borderColor: 'divider', gap: 2 }}>
        {result ? (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Concluir
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={saving}
              sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Criar empresa
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateTenantDialog;
